import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n.js';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import { useAuth } from '../context/AuthContext.jsx';
import logo from '../assets/logo_leela.jpg';
import {
  buildLocalizedPath,
  detectLanguageFromPath,
  resolveLanguageFromParam,
} from '../utils/languageRouting.js';
import { getLocalizedProfileField } from '../utils/profileTranslations.js';
import useWalletBalance from '../hooks/useWalletBalance.js';
import SupportRequestWidget from './SupportRequestWidget.jsx';

const SidebarNav = ({
  t,
  menu,
  user,
  sidebarUserName,
  currentLanguage,
  languageOptions,
  handleLanguageChange,
  getLocalizedPath,
  onNavigate,
  logout,
}) => {
  const renderMenuItem = (item) => {
    if (item.items) {
      return (
        <div key={item.labelKey} className="sidebar-group">
          <p className="sidebar-group-title">{t(item.labelKey)}</p>
          <div className="sidebar-submenu">
            {item.items.map((child) => (
              <NavLink
                key={child.path}
                to={getLocalizedPath(child.path)}
                className={({ isActive }) =>
                  isActive ? 'sidebar-link active' : 'sidebar-link'
                }
                onClick={onNavigate}
              >
                {t(child.labelKey)}
              </NavLink>
            ))}
          </div>
        </div>
      );
    }
    return (
      <NavLink
        key={item.path}
        to={getLocalizedPath(item.path)}
        className={({ isActive }) =>
          isActive ? 'sidebar-link top-level active' : 'sidebar-link top-level'
        }
        onClick={onNavigate}
      >
        {t(item.labelKey)}
      </NavLink>
    );
  };

  return (
    <>
      <div>
        <div className="sidebar-header">
          <div className="sidebar-brand-row">
            <NavLink
              to={getLocalizedPath('/dashboard')}
              className="sidebar-logo-link"
              aria-label={t('platform.caption')}
              onClick={onNavigate}
            >
              <span className="sidebar-logo-text">Leela Space</span>
            </NavLink>
            <label className="sidebar-language-compact" htmlFor="ui-language">
              <span className="visually-hidden">{t('platform.languageLabel')}</span>
              <select
                id="ui-language"
                className="sidebar-language-compact-select"
                value={currentLanguage}
                onChange={handleLanguageChange}
                aria-label={t('platform.languageLabel')}
              >
                {languageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div>
            <h2>{t('platform.dashboardTitle')}</h2>
          </div>
          {user && <span className="sidebar-user">{sidebarUserName || user.name}</span>}
        </div>
        <nav className="sidebar-nav">
          {menu.map((item) => renderMenuItem(item))}
        </nav>
      </div>
      <button type="button" className="sidebar-logout" onClick={logout}>
        {t('platform.logout')}
      </button>
    </>
  );
};

const MobileHeader = ({ t, balanceLabel, onOpenMenu, getLocalizedPath }) => (
  <header className="mobile-header">
    <button
      type="button"
      className="mobile-header__burger"
      onClick={onOpenMenu}
      aria-label="Открыть меню"
    >
      <span />
      <span />
      <span />
    </button>
    <NavLink
      to={getLocalizedPath('/dashboard')}
      className="mobile-header__logo"
      aria-label={t('platform.caption')}
    >
      <img src={logo} alt={t('platform.caption')} />
    </NavLink>
    <div className="mobile-header__balance" title={balanceLabel}>
      {balanceLabel}
    </div>
  </header>
);

const MobileDrawer = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="mobile-drawer" role="dialog" aria-modal="true">
      <aside className="mobile-drawer__panel">
        <div className="mobile-drawer__header">
          <span>{title}</span>
          <button type="button" className="mobile-drawer__close" onClick={onClose} aria-label="Закрыть меню">
            ×
          </button>
        </div>
        <div className="mobile-drawer__content">{children}</div>
      </aside>
      <button type="button" className="mobile-drawer__overlay" onClick={onClose} aria-label="Закрыть меню" />
    </div>
  );
};

const PlatformLayout = () => {
  const { t } = useTranslation();
  const { user, token, logout, updateProfile, bonusInfo, consumeBonuses } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguageNavigation();
  const langFromUrl = detectLanguageFromPath(location.pathname);
  const isProfileSettingsRoute = location.pathname.includes('/dashboard/settings/profile');
  const [showBonusBanner, setShowBonusBanner] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const hasActiveBonus = useMemo(
    () => Boolean(bonusInfo?.welcomeBonusApplied || bonusInfo?.referralBonusApplied),
    [bonusInfo],
  );

  useEffect(() => {
    setShowBonusBanner(hasActiveBonus);
  }, [hasActiveBonus]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isProfileSettingsRoute) {
      document.body.classList.remove('profile-settings-scroll-lock');
      return undefined;
    }

    document.body.classList.add('profile-settings-scroll-lock');
    return () => {
      document.body.classList.remove('profile-settings-scroll-lock');
    };
  }, [isProfileSettingsRoute]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      document.body.classList.remove('mobile-menu-open');
      return undefined;
    }

    document.body.classList.add('mobile-menu-open');
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  const { walletBalance, walletLoading } = useWalletBalance(token);

  const handleCloseBonus = () => {
    setShowBonusBanner(false);
    consumeBonuses();
  };

  const currentLanguage = resolveLanguageFromParam(langFromUrl || i18n.language);
  const sidebarUserName = user ? getLocalizedProfileField(user, 'name', currentLanguage) : '';

  const handleLanguageChange = async (event) => {
    const newLanguage = resolveLanguageFromParam(event.target.value);
    const currentLang = resolveLanguageFromParam(langFromUrl || i18n.language);

    if (newLanguage === currentLang) {
      return;
    }

    const currentFullPath = `${location.pathname}${location.search}${location.hash}`;
    const nextPath = buildLocalizedPath(location.pathname, newLanguage);
    const nextFullPath = `${nextPath}${location.search}${location.hash}`;

    if (nextPath && nextFullPath !== currentFullPath) {
      console.debug('[LANG-NAV]', {
        from: currentFullPath,
        to: nextFullPath,
        reason: 'language switch request',
        langFromUrl,
        i18nLang: i18n.language,
      });
      navigate(nextFullPath, { replace: true });
    }

    try {
      await updateProfile({ uiLanguage: newLanguage });
    } catch (error) {
      console.error('Не удалось обновить язык интерфейса', error);
      window.alert('Не удалось сохранить язык интерфейса. Попробуйте позже.');
    }
  };

  const languageOptions = [
    { value: 'ua', label: 'UA' },
    { value: 'ru', label: 'RU' },
    { value: 'en', label: 'EN' },
  ];

  const menu = [
    { labelKey: 'menu.createGame', path: '/dashboard/create-game' },
    { labelKey: 'menu.balance', path: '/dashboard/balance' },
    { labelKey: 'menu.prices', path: '/dashboard/prices' },
    {
      labelKey: 'menu.games.title',
      items: [
        { labelKey: 'menu.games.individual', path: '/dashboard/games/individual' },
        { labelKey: 'menu.games.group', path: '/dashboard/games/group' },
        { labelKey: 'menu.games.personal', path: '/dashboard/games/personal' },
      ],
    },
    {
      labelKey: 'menu.collections.title',
      items: [
        { labelKey: 'menu.collections.currentDeck', path: '/dashboard/my-cards' },
        { labelKey: 'menu.collections.myBoards', path: '/dashboard/my-boards' },
      ],
    },
    {
      labelKey: 'menu.settings.title',
      items: [
        { labelKey: 'menu.settings.profile', path: '/dashboard/settings/profile' },
        { labelKey: 'menu.settings.security', path: '/dashboard/settings/security' },
      ],
    },
    { labelKey: 'menu.referralProgram', path: '/dashboard/referral' },
  ];

  const balanceLabel = useMemo(() => {
    let formatter;
    try {
      formatter = new Intl.NumberFormat(currentLanguage || 'ua', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } catch (error) {
      formatter = new Intl.NumberFormat('en', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
    if (walletLoading) {
      return '...';
    }
    return `${formatter.format(walletBalance)} €`;
  }, [currentLanguage, walletBalance, walletLoading]);

  return (
    <div className="platform-layout">
      <MobileHeader
        t={t}
        balanceLabel={balanceLabel}
        onOpenMenu={() => setIsMobileMenuOpen(true)}
        getLocalizedPath={getLocalizedPath}
      />
      <aside className="platform-sidebar">
        <SidebarNav
          t={t}
          menu={menu}
          user={user}
          sidebarUserName={sidebarUserName}
          currentLanguage={currentLanguage}
          languageOptions={languageOptions}
          handleLanguageChange={handleLanguageChange}
          getLocalizedPath={getLocalizedPath}
          onNavigate={undefined}
          logout={logout}
        />
      </aside>
      <MobileDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        title={t('platform.dashboardTitle')}
      >
        <SidebarNav
          t={t}
          menu={menu}
          user={user}
          sidebarUserName={sidebarUserName}
          currentLanguage={currentLanguage}
          languageOptions={languageOptions}
          handleLanguageChange={handleLanguageChange}
          getLocalizedPath={getLocalizedPath}
          onNavigate={() => setIsMobileMenuOpen(false)}
          logout={logout}
        />
      </MobileDrawer>
      <main className={`platform-content${isProfileSettingsRoute ? ' platform-content--profile' : ''}`}>
        {showBonusBanner && hasActiveBonus && (
          <div className="bonus-toast" role="status" aria-live="polite">
            <div className="bonus-toast__content">
              <p className="bonus-toast__line">{t('bonusToast.welcome')}</p>
              {bonusInfo?.referralBonusApplied && (
                <p className="bonus-toast__line">{t('bonusToast.referral')}</p>
              )}
            </div>
            <button
              type="button"
              className="bonus-toast__close"
              onClick={handleCloseBonus}
              aria-label={t('bonusToast.close')}
            >
              ×
            </button>
          </div>
        )}
        <Outlet />
      </main>
      {['conductor', 'super'].includes(user?.role) && <SupportRequestWidget token={token} />}
    </div>
  );
};

export default PlatformLayout;
